import React, { useEffect, useRef } from 'react';
import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';
import glassesModel from './assets/glasses.glb';
import circleIndicator from './assets/circleIndicator.gif';
import { handleAnimation, updateLegsVisibility } from './Animation';

const GlassesViewer = ({ onAnimationUpdate }) => {
  const containerRef = useRef(null);
  const sceneRef = useRef(null);
  const cameraRef = useRef(null);
  const rendererRef = useRef(null);
  const glassesRef = useRef(null);
  const indicatorRef = useRef(null);
  const animationPhaseRef = useRef('zoom');
  const currentStepRef = useRef(0);
  const TOTAL_STEPS = 500; 

  const initializeEmissiveMaterials = (model) => {
    const componentColors = {
      'Camera_Rims_Left': 0x737373,    
      'Camera_Lens_Left': 0x3000a4,   
      'Inner_Rims_Left': 0x21FF00,   
      'Camera_Glasses_Left': 0x3366ff 
    };

    Object.entries(componentColors).forEach(([name, color]) => {
      const component = model.getObjectByName(name);
      if (component && component.material) {
        const newMaterial = component.material.clone();
        newMaterial.emissive = new THREE.Color(color);
        newMaterial.emissiveIntensity = 1;
        component.material = newMaterial;
      }
    });
  };

  const setupModelMaterials = (model) => {
    initializeEmissiveMaterials(model);

    model.traverse((child) => {
      if (child.name === 'legs_transparent' || 
          child.name.toLowerCase().includes('legs_transparent')) {
        if (child.material) {
          child.material.transparent = true;
          child.material.opacity = 0.2;
          child.material.side = THREE.DoubleSide;
          child.material.depthWrite = false;
        }
      }
      
      if (
        child.name === 'legs_transparent' || 
        child.name === 'logo_2' ||
        child.name.toLowerCase().includes('legs_transparent')
      ) {
        child.visible = false;
      }
    });
  };

  const updateIndicatorPosition = () => {
    if (!glassesRef.current || !indicatorRef.current || !cameraRef.current || !rendererRef.current) return;

    const modelPosition = new THREE.Vector3();
    glassesRef.current.getWorldPosition(modelPosition);
    
    const point = new THREE.Vector3(-8.9, 1.6, 2);
    const rotationMatrix = new THREE.Matrix4();
    rotationMatrix.makeRotationY(glassesRef.current.rotation.y);
    point.applyMatrix4(rotationMatrix);
    
    const finalPosition = new THREE.Vector3();
    finalPosition.addVectors(modelPosition, point);
    
    const screenPosition = finalPosition.clone();
    screenPosition.project(cameraRef.current);

    const x = (screenPosition.x * 0.5 + 0.5) * rendererRef.current.domElement.clientWidth;
    const y = -(screenPosition.y * 0.5 - 0.5) * rendererRef.current.domElement.clientHeight;

    indicatorRef.current.style.transform = `translate(${x}px, ${y}px)`;
    indicatorRef.current.style.left = '0';
    indicatorRef.current.style.top = '0';
  };

  useEffect(() => {
    if (!containerRef.current) return;

    // Scene setup
    const scene = new THREE.Scene();
    sceneRef.current = scene;
    scene.background = null;

    // Camera setup
    const camera = new THREE.PerspectiveCamera(
      75,
      window.innerWidth / window.innerHeight,
      0.1,
      1000
    );
    cameraRef.current = camera;
    camera.position.z = 2; 

    // Renderer setup
    const renderer = new THREE.WebGLRenderer({ 
      antialias: true,
      alpha: true 
    });
    rendererRef.current = renderer;
    renderer.setSize(window.innerWidth, window.innerHeight);
    containerRef.current.appendChild(renderer.domElement);

    // Create indicator overlay
    const indicator = document.createElement('img');
    indicator.src = circleIndicator;
    indicator.style.position = 'absolute';
    indicator.style.width = '56px';
    indicator.style.height = '56px';
    indicator.style.pointerEvents = 'none';
    indicator.style.opacity = '0';
    indicator.style.transform = 'translate(-50%, -50%)';
    containerRef.current.appendChild(indicator);
    indicatorRef.current = indicator;

    // Lighting
    const ambientLight = new THREE.AmbientLight(0xffffff, 1.0);
    scene.add(ambientLight);
    
    const directionalLight = new THREE.DirectionalLight(0xffffff, 2.0);
    directionalLight.position.set(20, 20, 20);
    scene.add(directionalLight);

    const followLight = new THREE.SpotLight(0xffffff, 2);
    followLight.position.set(0, 20, 5); 
    followLight.angle = Math.PI / 1; 
    followLight.penumbra = 1; 
    followLight.decay = 0.5; 
    scene.add(followLight);

    const followLightBottom = new THREE.SpotLight(0xffffff, 2);
    followLightBottom.position.set(0, -40, 5); 
    followLightBottom.angle = Math.PI / 1; 
    followLightBottom.penumbra = 0.5; 
    followLightBottom.decay = 0.5; 
    scene.add(followLightBottom);

    // Load glasses model
    const loader = new GLTFLoader();
    loader.load(glassesModel, (gltf) => {
      const model = gltf.scene;
      glassesRef.current = model;
      scene.add(model);

      // Center the model
      const box = new THREE.Box3().setFromObject(model);
      const center = box.getCenter(new THREE.Vector3());
      model.position.sub(center);

      setupModelMaterials(model);

      model.rotation.y = 0;
    });

    // Controls
    const controls = new OrbitControls(camera, renderer.domElement);
    controls.enableDamping = true;
    controls.dampingFactor = 0.05;
    controls.enabled = false; // Disable manual controls

    // Animation loop
    const animate = () => {
      requestAnimationFrame(animate);

      if (camera && glassesRef.current) {
        const scrollPercent = window.scrollY / (document.documentElement.scrollHeight - window.innerHeight);
        const currentStep = Math.floor((scrollPercent - 0.14) * TOTAL_STEPS);
        currentStepRef.current = Math.max(0, Math.min(currentStep, TOTAL_STEPS));
        
        handleAnimation(
          currentStepRef.current,
          camera,
          glassesRef.current,
          indicatorRef,
          updateIndicatorPosition,
          animationPhaseRef
        );

        if (glassesRef.current) {
          glassesRef.current.visible = currentStepRef.current > 3 && camera.position.z > 0 && animationPhaseRef.current !== 'hide';
        }

        if (glassesRef.current) {
          const modelPosition = glassesRef.current.position;
          followLight.position.set(
            modelPosition.x,
            modelPosition.y + 2,
            modelPosition.z + 5
          );
          followLight.target = glassesRef.current;

          followLightBottom.position.set(
            modelPosition.x,
            modelPosition.y - 2,
            modelPosition.z + 5
          );
          followLightBottom.target = glassesRef.current;
        }

        onAnimationUpdate({
          phase: animationPhaseRef.current,
          scrollPercent: scrollPercent.toFixed(3),
          cameraZ: camera.position.z.toFixed(2),
          modelRotationY: (glassesRef.current.rotation.y * (180/Math.PI)).toFixed(2),
          currentStep: currentStepRef.current
        });
      }

      controls.update();
      renderer.render(scene, camera);
    };
    animate();

    const handleResize = () => {
      if (!camera || !renderer) return;
      
      camera.aspect = window.innerWidth / window.innerHeight;
      camera.updateProjectionMatrix();
      renderer.setSize(window.innerWidth, window.innerHeight);

      updateIndicatorPosition();
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      if (indicatorRef.current) {
        containerRef.current?.removeChild(indicatorRef.current);
      }
      containerRef.current?.removeChild(renderer.domElement);
    };
  }, [onAnimationUpdate]);

  return <div ref={containerRef} style={{ height: '100vh', width: '100%' }} />;
};

export default GlassesViewer; 